import React from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { ExpandMore, Speed, ArrowForward } from '@mui/icons-material';
import { MigrationDecisionTool } from './MigrationDecisionTool';

interface MigrationOptionsTabProps {
  onNavigateToStepByStep: () => void;
}

export function MigrationOptionsTab({ onNavigateToStepByStep }: MigrationOptionsTabProps) {
  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Migration Options & Comparisons
      </Typography>

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">
            🎯 Interactive Migration Decision Tool
          </Typography>
        </AccordionSummary>        <AccordionDetails>
          <MigrationDecisionTool onNavigateToGuide={(framework: 'vite' | 'nextjs') => onNavigateToStepByStep()} />
        </AccordionDetails>
      </Accordion>

      <Accordion sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">
            📊 Quick Overview
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <Speed sx={{ mr: 1 }} />
                    CRA → Vite Migration
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Fastest development experience with minimal configuration
                  </Typography>
                  
                  <Chip label="Difficulty: Easy" color="success" size="small" sx={{ mr: 1, mb: 1 }} />
                  <Chip label="Time: 1-2 hours" color="info" size="small" sx={{ mb: 2 }} />
                  
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Best for:</Typography>
                  <List dense>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText primary="• SPAs with fast development needs" />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText primary="• Modern browser targeting" />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText primary="• Simple to medium complexity apps" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <ArrowForward sx={{ mr: 1 }} />
                    CRA → Next.js Migration
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Full-stack React framework with SSR/SSG capabilities
                  </Typography>
                  
                  <Chip label="Difficulty: Medium" color="warning" size="small" sx={{ mr: 1, mb: 1 }} />
                  <Chip label="Time: 4-8 hours" color="info" size="small" sx={{ mb: 2 }} />
                  
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Best for:</Typography>
                  <List dense>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText primary="• SEO-critical applications" />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText primary="• Full-stack development needs" />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText primary="• Performance-critical apps" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">
            🔍 Feature Comparison
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Feature</TableCell>
                  <TableCell align="center">CRA</TableCell>
                  <TableCell align="center">Vite</TableCell>
                  <TableCell align="center">Next.js</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Hot Module Reload</TableCell>
                  <TableCell align="center">✅</TableCell>
                  <TableCell align="center">⚡ Fast</TableCell>
                  <TableCell align="center">✅</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Server-Side Rendering</TableCell>
                  <TableCell align="center">❌</TableCell>
                  <TableCell align="center">❌</TableCell>
                  <TableCell align="center">✅</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Static Site Generation</TableCell>
                  <TableCell align="center">❌</TableCell>
                  <TableCell align="center">✅ Plugin</TableCell>
                  <TableCell align="center">✅</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>API Routes</TableCell>
                  <TableCell align="center">❌</TableCell>
                  <TableCell align="center">❌</TableCell>
                  <TableCell align="center">✅</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Built-in Optimization</TableCell>
                  <TableCell align="center">✅</TableCell>
                  <TableCell align="center">✅</TableCell>
                  <TableCell align="center">⚡ Advanced</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
